import { useProcess } from '@fasstech/oip-starter-utils/app';
import { useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { isNilOrEmpty, isNotNilOrEmpty } from '@@serverHelpers/index.mjs';
import useGetSimulationCompanySearchCompanyQuery from './useGetSimulationCompanySearchCompanyQuery.js';
import { isSiretInvalid } from '@@viewHelpers/validations/siret.js';
import { compose, find, isEmpty, isNil, map, not, path, pathOr, prop, propEq, sortBy, trim, filter, propOr, defaultTo } from 'ramda';
import useSaveSimulationCompanyMutation from './useSaveSimulationCompanyMutation.js';
import useGetSimulationCompanySearchCompanyCcnsQuery from './useGetSimulationCompanySearchCompanyCcnsQuery.js';
import useGetSimulationCompanySearchCompanyOtherCcnsQuery from './useGetSimulationCompanySearchCompanyOtherCcnsQuery.js';
import useGetSimulationCompanyCcnOfferQuery from './useGetSimulationCompanyCcnOfferQuery.js';
import useGetSimulationCompanyFlexiproductQuery from '@@views/simulationCompany/client/hooks/useGetSimulationCompanyFlexiproductQuery.js';
import { parseCcnOfferResponse } from './helpers/parseCcnOfferResponse.js';
import { formatCcnValues } from './helpers/formatCcnValues.js';
import { formatCompanyValues } from './helpers/formatCompanyValues.js';
import { CollegeEnum } from '@fasstech/oip-core/entities/enums';

const useSimulationCompanyForm = ({ company, selectedCompany }) => {
  const { processHandlers, context } = useProcess();
  const [loading, setLoading] = useState(false);
  const [loadingCcns, setLoadingCcns] = useState(false);
  const [loadingOtherCcns, setLoadingOtherCcns] = useState(false);
  const [loadingCcnOffer, setLoadingCcnOffer] = useState(false);
  const [companies, setCompaniesState] = useState(null);
  const [noCCN, setNoCCN] = useState(false);
  const [ccns, setCcns] = useState(null);
  const [otherCcns, setOtherCcns] = useState(null);
  const [noCompaniesFound, setNoCompaniesFound] = useState(false);
  const [ccnOffer, setCcnOffer] = useState(null);
  const [ccnHasAgreement, setCcnHasAgreement] = useState(null);
  const [agreementIsCompliant, setAgreementIsCompliant] = useState(null);
  const [isEmptyFlexiproduct, setIsEmptyFlexiproduct] = useState(null);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [unauthorizedModalMessage, setUnauthorizedModalMessage] = useState('');
  const [validatedCcn, setValidatedCcn] = useState(null);
  const [validatedCcnRef, setValidatedCcnRef] = useState(null);
  const [redirectionInfo, setRedirectionInfo] = useState(null);
  const [canGoNext, setCanGoNext] = useState(false);

  const setCompanies = (companies) => {
    setNoCCN(false);
    setCompaniesState(companies);
  };

  useEffect(() => {
    if (ccnOffer && ccnHasAgreement && agreementIsCompliant) {
      // Offre moteur & comparatif et formule OK
      setCanGoNext(true);
      return;
    }
    if (ccnOffer && isEmptyFlexiproduct) {
      // Eligible et retour vide de Flexiproduct
      setCanGoNext(true);
      return;
    }
    setCanGoNext(false);
  }, [ccnOffer, ccnHasAgreement, agreementIsCompliant, isEmptyFlexiproduct]);

  const { onFormSubmit } = useSaveSimulationCompanyMutation();

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      company: {
        ccnRef: path(['company', 'ccnRef'], company),
        history: {
          name: path(['company', 'history', 'name'], company) ?? path(['company', 'name'], company),
          siret: path(['company', 'history', 'siret'], company) ?? path(['company', 'siret'], company),
          department: path(['company', 'history', 'department'], company) ?? path(['company', 'addresses', 0, 'department'], company)
        }
      }
    }
  });

  const { isValid } = form.formState;

  const search = isNotNilOrEmpty(form.watch('company.history.siret'))
    ? form.watch('company.history.siret')
    : form.watch('company.history.name');
  const department = form.watch('company.history.department');

  const { refetch } = useGetSimulationCompanySearchCompanyQuery({ skip: true });
  const { refetch: refetchCcns } = useGetSimulationCompanySearchCompanyCcnsQuery({ skip: true });
  const { refetch: refetchOtherCcns } = useGetSimulationCompanySearchCompanyOtherCcnsQuery({ skip: true });
  const { refetch: refetchCcnOffer } = useGetSimulationCompanyCcnOfferQuery({ skip: true });
  const { refetch: refetchFlexiproduct } = useGetSimulationCompanyFlexiproductQuery({ skip: true });

  useEffect(() => {
    if (
      isNotNilOrEmpty(path(['history', 'siret'], selectedCompany)) ||
      (isNotNilOrEmpty(path(['history', 'name'], selectedCompany)) &&
        isNotNilOrEmpty(path(['history', 'department'], selectedCompany)))
    ) {
      onSearch();
    }
    if (
      isNotNilOrEmpty(selectedCompany.history) &&
      isNotNilOrEmpty(selectedCompany.siret)
    ) {
      onSearchCcns({ company: selectedCompany });
    }
    if (
      isNotNilOrEmpty(selectedCompany.history) &&
      isNilOrEmpty(selectedCompany.ccnRef)
    ) {
      setNoCCN(true);
      onSearchCcnOffer();
      onSearchFlexiproduct();
    }
  }, []);

  const hasRunRef = useRef(false);
  const validatedCcnIdRef = useRef(null);
  useEffect(() => {
    if (hasRunRef.current) return;
    if (
      isNotNilOrEmpty(form.watch('company.ccnRef')) &&
      isNotNilOrEmpty(ccns) &&
      isNil(find(propEq('ref', form.watch('company.ccnRef')), ccns || []))
    ) {
      onSearchOtherCcns();
    }
  }, [ccns]);

  const onSearch = () => {
    setLoading(true);
    setCcns(null);
    setCcnOffer(null);
    refetch({ search, department })
      .then(({ data }) => {
        const companies = pathOr(
          [],
          ['getSimulationCompanySearchCompany_simulation_company', 'companies'],
          data
        );
        setCompanies(companies);
        setNoCompaniesFound(companies.length === 0);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Erreur lors de la recherche d\'entreprise: ', err);
        setCompanies(null);
        setNoCompaniesFound(true);
        setLoading(false);
      });
  };

  const collegeOptions = [
    { label: 'Ensemble du personnel', value: CollegeEnum.ALL },
    { label: 'Cadres', value: CollegeEnum.EXECUTIVE_EMPLOYEES },
    { label: 'Non cadres', value: CollegeEnum.EMPLOYEES }
  ];

  const onSelectCcn = (ccnRef) => {
    setRedirectionInfo(null);
    const resolvedCcn = defaultTo(
      {},
      find(propEq('ref', ccnRef), ccns || []) ||
      find(propEq('ref', ccnRef), otherCcns || [])
    );
    const college =
      find(propEq('label', pathOr('', ['colleges', 0], resolvedCcn)))(collegeOptions)
      || collegeOptions[0];
    onSearchCcnOffer(resolvedCcn.idcc, college.value, ccnRef, resolvedCcn);
  };

  const onSearchCcns = ({ company }) => {
    setLoadingCcns(true);
    refetchCcns({ siret: company.siret })
      .then(({ data }) => {
        setCcns(
          pathOr(
            [],
            ['getSimulationCompanySearchCompanyCcns_simulation_company', 'ccns'],
            data
          )
        );
        setLoadingCcns(false);
      })
      .catch((err) => {
        console.error('Erreur lors de la recherche d\'entreprise: ', err);
        setCcns(null);
        setLoadingCcns(false);
      });
  };

  const onSearchOtherCcns = () => {
    setLoadingOtherCcns(true);
    refetchOtherCcns({})
      .then(({ data }) => {
        setOtherCcns(
          pathOr(
            [],
            ['getSimulationCompanySearchCompanyOtherCcns_simulation_company', 'ccns'],
            data
          )
        );
        setLoadingOtherCcns(false);
      })
      .catch((err) => {
        console.error('Erreur lors de la recherche d\'entreprise: ', err);
        setOtherCcns(null);
        setLoadingOtherCcns(false);
      });
  };

  const onSearchCcnOffer = (ccn = '', college = '', ccnRef = '', originalCcn = null) => {
    setLoadingCcnOffer(true);
    refetchCcnOffer({ ccn, college, ccnRef })
      .then(({ data }) => {
        const { ccnId, ccnRef, ccnOffer, ccnName, redirection, oldCcnId, oldCcnName, comment } = parseCcnOfferResponse(data);
        setValidatedCcn(ccnId);
        setValidatedCcnRef(ccnRef);
        if (ccnRef) form.setValue('company.ccnRef', ccnRef);
        if (ccnName) form.setValue('company.ccnName', ccnName);
        setCcnOffer(ccnOffer);
        if (redirection && oldCcnId && oldCcnName) {
          setRedirectionInfo({
            name: ccnName,
            idcc: ccnId,
            oldName: oldCcnName,
            oldIdcc: oldCcnId,
            comment
          });
        } else {
          setRedirectionInfo(null);
        }
        // on appelle flexiproduct avec le bon idcc selon redirection ou non
        onSearchFlexiproduct(ccnId || ccn);
        setLoadingCcnOffer(false);
      })
      .catch((err) => {
        console.error('Erreur lors de la recherche d\'offre CCN:', err);
        setCcnOffer(null);
        setLoadingCcnOffer(false);
      });
  };

  const onSearchFlexiproduct = (idcc = '') => {
    refetchFlexiproduct({ idcc })
      .then(({ data }) => {
        if (path([
          'getSimulationCompanyFlexiproduct_simulation_company',
          'error'
        ],
        data
        )) {
          setIsUnauthorized(true);
          setUnauthorizedModalMessage(
            'Suite indisponibilité technique, les documents et/ou les informations produits ne sont pas disponibles. Veuillez réessayer ultérieurement. Nous mettons tout en œuvre pour résoudre le problème. En nous excusant de la gêne occasionnée.'
          );
        }
        setCcnHasAgreement(
          pathOr(
            null,
            ['getSimulationCompanyFlexiproduct_simulation_company', 'ccnHasAgreement'],
            data
          )
        );
        setAgreementIsCompliant(
          pathOr(
            null,
            ['getSimulationCompanyFlexiproduct_simulation_company', 'agreementIsCompliant'],
            data
          )
        );
        setIsEmptyFlexiproduct(
          pathOr(
            null,
            ['getSimulationCompanyFlexiproduct_simulation_company', 'isEmptyFlexiproduct'],
            data
          )
        );

      })
      .catch((err) => {
        console.error('Erreur lors de la recherche d\'offre FlexiProduct: ', err);
        setCcnHasAgreement(null);
        setAgreementIsCompliant(null);
      });
  };

  const onSubmit = (values) => {
    const ccnValues = formatCcnValues({
      noCCN,
      validatedCcn,
      validatedCcnRef,
      ccnRef: values.company.ccnRef,
      ccnName: values.company.ccnName,
      ccns,
      otherCcns
    });

    const formattedValues = formatCompanyValues({
      selectedCompany,
      ccnValues,
      ccnHasAgreement,
      historyValues: values.company.history
    });

    onFormSubmit(formattedValues, (ok, err) => {
      if (ok && !err) {
        processHandlers.onNext({
          context: { projectId: context?.projectId }
        });
      }
    });
  };

  const options = map(
    (item) => ({ label: item.name, value: item.ref }),
    ccns || []
  );

  let optionsOtherCcns = [];
  const sortAlphabeticlyByName = sortBy(prop('name'));
  if (!isNil(otherCcns) && not(isEmpty(otherCcns))) {
    optionsOtherCcns = compose(
      sortAlphabeticlyByName,
      map((ccn) => ({
        name: trim(propOr('', 'name', ccn)),
        idcc: ccn.idcc,
        ref: ccn.ref,
        label: `${ccn.idcc} - ${ccn.name}`,
        value: ccn.ref
      })),
      filter(Boolean)
    )(otherCcns);
  }

  const isNameEmpty = isNilOrEmpty(form.watch('company.history.name'));
  const isDepartmentEmpty = isNilOrEmpty(form.watch('company.history.department'));
  const siret = form.watch('company.history.siret') || '';
  const disableSearch = isSiretInvalid(siret) && (isNameEmpty || isDepartmentEmpty);

  return {
    form,
    isValid,
    loading,
    onSearch,
    onSearchCcns,
    onSearchOtherCcns,
    companies,
    setCompanies,
    ccns,
    loadingCcns,
    otherCcns,
    loadingOtherCcns,
    disableSearch,
    onSubmit,
    noCompaniesFound,
    setCcns,
    noCCN,
    setNoCCN,
    options,
    optionsOtherCcns,
    onSearchCcnOffer,
    ccnOffer,
    loadingCcnOffer,
    setCcnOffer,
    setOtherCcns,
    onSearchFlexiproduct,
    isUnauthorized,
    unauthorizedModalMessage,
    setUnauthorizedModalMessage,
    validatedCcn,
    validatedCcnRef,
    setValidatedCcn,
    redirectionInfo,
    validatedCcnIdRef,
    onSelectCcn,
    canGoNext
  };
};

export default useSimulationCompanyForm;
